/**
 * Centralized SEO configuration for all routes.
 * Adds keyword-rich titles, descriptions, canonical URLs, Open Graph tags,
 * and page-specific JSON-LD (Service / AutoDetailing / BreadcrumbList) to every page.
 */

import {
  marineDetailingFAQ,
  planeDetailingFAQ,
  carWrappingFAQ,
  corporateFleetFAQ,
} from '../data/serviceFAQs';
import reviewsData from '../data/reviews';
import { TINT_FAQS } from '../data/windowTint';

const SITE_URL = 'https://ssfdetail.com';
const DEFAULT_OG = 'https://customer-assets.emergentagent.com/job_carcare-elite/artifacts/vb3klkvj_Award%20Winning%20PPF%20And%20Ceramic%20Coating%20In%20Boca%20Raton%2C%20FL%20%20SSF%20Detail.png';

const PROVIDER = {
  '@type': 'LocalBusiness',
  '@id': 'https://ssfdetail.com/#business',
  name: 'SSF Detail',
  telephone: '+1-561-489-7527',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '850 NW 1st Ave',
    addressLocality: 'Boca Raton',
    addressRegion: 'FL',
    postalCode: '33432',
    addressCountry: 'US',
  },
};

const DEFAULT_AREAS = [
  { '@type': 'City', name: 'Boca Raton' },
  { '@type': 'City', name: 'Delray Beach' },
  { '@type': 'City', name: 'Boynton Beach' },
  { '@type': 'City', name: 'Deerfield Beach' },
  { '@type': 'City', name: 'Pompano Beach' },
  { '@type': 'City', name: 'Coral Springs' },
  { '@type': 'City', name: 'Coconut Creek' },
  { '@type': 'City', name: 'Parkland' },
  { '@type': 'City', name: 'Wellington' },
  { '@type': 'City', name: 'Fort Lauderdale' },
  { '@type': 'City', name: 'West Palm Beach' },
  { '@type': 'City', name: 'Jupiter' },
  { '@type': 'City', name: 'Lighthouse Point' },
  { '@type': 'City', name: 'Hillsboro Beach' },
  { '@type': 'City', name: 'Aventura' },
  { '@type': 'City', name: 'Miami' },
  { '@type': 'City', name: 'Tampa' },
  { '@type': 'AdministrativeArea', name: 'Palm Beach County, FL' },
  { '@type': 'AdministrativeArea', name: 'Broward County, FL' },
  { '@type': 'AdministrativeArea', name: 'Miami-Dade County, FL' },
];

const RATING = {
  '@type': 'AggregateRating',
  ratingValue: '5.0',
  reviewCount: '150',
  bestRating: '5',
  worstRating: '1',
};

const breadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

const serviceSchema = ({ name, description, path, serviceType, image, priceRange, offers }) => {
  const base = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType: serviceType || name,
    url: `${SITE_URL}${path}`,
    ...(image && { image }),
    ...(priceRange && { priceRange }),
    provider: PROVIDER,
    areaServed: DEFAULT_AREAS,
    aggregateRating: RATING,
  };
  if (Array.isArray(offers) && offers.length) {
    base.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: `${name} Packages`,
      itemListElement: offers.map((o) => ({
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: o.price,
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}${path}`,
        itemOffered: {
          '@type': 'Service',
          name: o.name,
          description: o.desc,
        },
      })),
    };
  }
  return base;
};

const locationSchema = ({ city, path }) => ({
  '@context': 'https://schema.org',
  '@type': 'AutoDetailing',
  name: `SSF Detail — Auto Detailing in ${city}, FL`,
  description: `Premium mobile and shop-based car detailing, ceramic coating, paint protection film, and window tinting serving ${city}, Florida.`,
  url: `${SITE_URL}${path}`,
  telephone: '+1-561-489-7527',
  image: DEFAULT_OG,
  address: PROVIDER.address,
  priceRange: '$$$',
  areaServed: {
    '@type': 'City',
    name: city,
    containedInPlace: { '@type': 'AdministrativeArea', name: 'Florida' },
  },
  aggregateRating: RATING,
});

/* ---------------- Per-route SEO ---------------- */

const SERVICE_PAGES = {
  '/car-detailing': {
    title: 'Car Detailing Boca Raton, FL | South Florida | SSF Detail',
    description:
      "South Florida's top-rated car detailing — full interior + exterior detail, paint correction, maintenance memberships, and mobile service across Boca Raton, Delray Beach, Fort Lauderdale, and Palm Beach County. 150+ 5-star Google reviews. Same-day quotes.",
    keywords:
      'car detailing Boca Raton, car detailing South Florida, auto detailing Delray Beach, mobile car detailing Boca Raton, full car detail Fort Lauderdale, interior detailing South Florida, exterior detailing Palm Beach County, paint correction Boca Raton, luxury car detailing Broward County, exotic car detailing Florida, best car detailer South Florida, car detailing near me Boca Raton, monthly detailing membership Florida',
    schemaName: 'Premium Car Detailing',
    schemaType: 'Automotive Detailing',
    schemaDesc:
      'Full interior and exterior auto detailing, monthly memberships, paint correction, and luxury car care across Boca Raton, Delray Beach, Fort Lauderdale and South Florida.',
    priceRange: '$75 - $395',
    offers: [
      { name: 'Ceramic Safe Car Wash', price: '75', desc: 'Graphene hand wash, foam bath, wheel cleaning, glass and tire dressing.' },
      { name: 'Full Detail', price: '225', desc: 'Interior + exterior decontamination, vacuum, leather care, paint sealant.' },
      { name: 'Full Detail PLUS', price: '325', desc: 'Premium full detail with steam clean, deep interior reset, and longer-lasting sealant.' },
      { name: 'SSF Detail Membership (Monthly)', price: '199', desc: 'Monthly recurring Full Detail keeping your vehicle show-room ready year-round.' },
    ],
  },
  '/ceramic-coating': {
    title: 'Ceramic Coating Boca Raton, FL | Professional Installation | SSF Detail',
    description:
      'Professional Kovalent ceramic coating in Boca Raton, Delray, Fort Lauderdale, and Palm Beach County — 5, 7 and 9-year graphene and borophene options. Maximum gloss, hydrophobic water beading, UV defense. 150+ 5-star reviews. Free same-day quotes.',
    keywords:
      'ceramic coating Boca Raton, ceramic coating South Florida, ceramic coating Delray Beach, ceramic coating Fort Lauderdale, ceramic coating West Palm Beach, Kovalent ceramic coating, graphene coating Florida, 9 year ceramic coating, 7 year graphene coating, nano ceramic coating Boca Raton, hydrophobic coating South Florida, best ceramic coating Palm Beach County, professional ceramic coating Broward County, ceramic coating near me, Borophene ceramic coating',
    schemaName: 'Ceramic Coating Installation',
    schemaType: 'Automotive Paint Protection',
    schemaDesc:
      'Professional Kovalent ceramic coating with 5, 7, and 9-year durability tiers including graphene and boron nitride formulations. Boca Raton, Delray, Fort Lauderdale, Palm Beach County, Broward County.',
    priceRange: '$1,149 - $2,249',
    offers: [
      { name: 'Basic Ceramic', price: '1149', desc: 'Single-layer Kovalent ceramic coating on paint, plastics and glass — 5-year durability, ideal for newer well-kept vehicles.' },
      { name: 'Advanced Graphene — Best Seller', price: '1599', desc: 'Two-layer Kovalent Graphene coating with single-stage paint correction, wheel-face and glass coating — 7-year durability.' },
      { name: 'Pro Graphene / Borophene', price: '2249', desc: 'Multi-stage paint correction with full-vehicle graphene / boron-nitride coating including calipers and fender liners — 9-year durability, self-healing.' },
    ],
  },
  '/about-ceramic-coating': {
    title: 'What Is Ceramic Coating? Pricing, Packages & Pre-Sell Guide | SSF Detail',
    description:
      "Everything South Florida owners need to know about Kovalent ceramic coating — what it does, why it beats wax, package pricing (5 / 7 / 9-year), and how to book in Boca Raton, Delray, Fort Lauderdale, and West Palm Beach. 150+ 5-star reviews, 1,350+ vehicles serviced.",
    keywords:
      'what is ceramic coating, ceramic coating cost Boca Raton, ceramic coating cost South Florida, ceramic coating packages, ceramic coating vs wax, graphene coating Florida, best ceramic coating South Florida, professional ceramic coating Palm Beach County, ceramic coating Boca Raton pricing, Kovalent ceramic coating cost, 9 year ceramic coating price',
    schemaName: 'Ceramic Coating Consultation',
    schemaType: 'Automotive Paint Protection',
    schemaDesc:
      'In-depth ceramic coating guide with Kovalent package pricing, comparison vs wax & DIY spray ceramics, FAQ, and booking — for Boca Raton, South Florida, and Tampa Bay.',
  },
  '/paint-protection-film': {
    title: 'Paint Protection Film (PPF) Boca Raton, FL | STEK Installer | SSF Detail',
    description:
      "Premium STEK paint protection film in Boca Raton, Delray, Fort Lauderdale, West Palm Beach. Self-healing, hydrophobic, impact-resistant clear bra for luxury and exotic vehicles. Starter, Full Front End, Track, and Full Body packages. 150+ 5-star reviews.",
    keywords:
      'paint protection film Boca Raton, paint protection film South Florida, PPF installation Boca Raton, PPF Fort Lauderdale, PPF Delray Beach, PPF West Palm Beach, STEK PPF Florida, clear bra Boca Raton, clear bra South Florida, car paint protection Palm Beach County, self-healing PPF Florida, exotic car PPF Broward County, full body PPF Boca Raton, PPF near me',
    schemaName: 'Paint Protection Film (PPF) Installation',
    schemaType: 'Paint Protection Film',
    schemaDesc:
      'Premium STEK paint protection film installation for luxury and exotic vehicles. Self-healing, hydrophobic, impact-resistant coverage across Boca Raton, Delray, Fort Lauderdale, and South Florida.',
    priceRange: '$1,499 - $9,500',
    offers: [
      { name: 'Starter PPF Kit', price: '1499', desc: 'Front bumper + side mirrors STEK PPF coverage.' },
      { name: 'Full Front End', price: '2999', desc: 'Hood, fenders, headlights, bumper and mirrors.' },
      { name: 'Track Package', price: '4499', desc: 'Full front + A-pillars + rear bumper + rocker panels.' },
      { name: 'Full Body PPF', price: '7499', desc: 'Complete vehicle paint protection film coverage.' },
    ],
  },
  '/ppf': {
    title: '#1 PPF Installation in South Florida | STEK Paint Protection Film | SSF Detail',
    description:
      'STEK paint protection film (PPF) installation in Boca Raton, Delray Beach, Fort Lauderdale, and Palm Beach County. Self-healing, hydrophobic, crystal-clear film engineered for Florida\'s climate.',
    keywords: 'PPF Boca Raton, PPF South Florida, STEK PPF installation, paint protection film Florida, clear bra installation Fort Lauderdale, PPF Delray Beach',
  },
  '/window-tint': {
    title: 'Window Tinting South Florida | Boca Raton | SSF Detail',
    description:
      'Ceramic and carbon window tinting in South Florida. Explore tint shades, heat rejection, privacy, and vehicle coverage at SSF Detail. Get a free tint quote.',
    keywords:
      'window tint Boca Raton, window tinting South Florida, ceramic tint Boca Raton, ceramic window tint Delray Beach, window tint Fort Lauderdale, window tint West Palm Beach, automotive window tinting Palm Beach County, car tint Broward County, UV block tint Florida, heat rejection tint South Florida, ceramic tint near me, Autobahn window tint',
    schemaName: 'Automotive Window Tinting',
    schemaType: 'Window Tinting',
    schemaDesc:
      'Ceramic and carbon window tinting for cars, SUVs, trucks, luxury and performance vehicles in South Florida. Two front windows, full vehicle tint, windshield tint strips, full windshield tint and windshield heat rejection film, subject to applicable legal restrictions. Serving Boca Raton, Deerfield Beach, Pompano Beach, Lighthouse Point, Coconut Creek, Fort Lauderdale, Coral Springs and Delray Beach.',
  },
  '/marine-detailing': {
    title: 'Yacht & Boat Detailing in Boca Raton, FL | SSF Detail',
    description:
      'Professional marine detailing for yachts and boats in Boca Raton and South Florida. Hull polishing, interior cleaning, gel coat restoration, and ceramic coating built for saltwater environments.',
    keywords:
      'yacht detailing Boca Raton, boat detailing South Florida, marine ceramic coating, hull polishing Florida, yacht cleaning Boca Raton, boat gel coat restoration',
    schemaName: 'Yacht & Boat Detailing',
    schemaType: 'Marine Detailing',
    schemaDesc:
      'Professional yacht and boat detailing including hull polishing, interior cleaning, gel coat restoration, and marine ceramic coating for South Florida.',
  },
  '/plane-detailing': {
    title: 'Aircraft & Private Jet Detailing in Boca Raton, FL | SSF Detail',
    description:
      'Interior and exterior detailing for private aircraft and jets. Meticulous care for leather cabins, avionics, exteriors, and aviation-grade finishes. Available across South Florida.',
    keywords:
      'aircraft detailing Florida, private jet detailing Boca Raton, airplane cleaning South Florida, aviation detailing, jet interior detailing',
    schemaName: 'Aircraft & Private Jet Detailing',
    schemaType: 'Aviation Detailing',
    schemaDesc:
      'Professional interior and exterior detailing for private aircraft and business jets across South Florida.',
  },
  '/car-wrapping': {
    title: 'Car Wrapping & Vinyl Wraps in Boca Raton, FL | SSF Detail',
    description:
      'Color change wraps, matte conversions, chrome delete, and custom vinyl installations in Boca Raton. Premium vinyl from leading brands for luxury and exotic vehicles.',
    keywords:
      'car wrap Boca Raton, vinyl wrap South Florida, color change wrap Florida, matte black wrap, chrome delete Boca Raton, paint protection wrap',
    schemaName: 'Car Wrapping & Vinyl Installation',
    schemaType: 'Vehicle Wrapping',
    schemaDesc:
      'Custom vinyl wraps including color change, matte conversions, chrome delete, and protective wraps for luxury and exotic vehicles.',
  },
  '/corporate-fleet': {
    title: 'Corporate & Fleet Vehicle Services in Boca Raton, FL | SSF Detail',
    description:
      'Scheduled detailing, ceramic coating, PPF, and window tinting for dealerships, corporate fleets, and executive transportation across South Florida.',
    keywords:
      'fleet detailing Boca Raton, corporate car detailing South Florida, dealership detailing Florida, executive vehicle services, fleet ceramic coating',
    schemaName: 'Corporate & Fleet Detailing Services',
    schemaType: 'Fleet Services',
    schemaDesc:
      'Scheduled maintenance detailing, ceramic coating, PPF, and window tint programs for dealerships, corporate fleets, and executive transportation.',
  },
};

const LOCATION_PAGES = {
  '/auto-detailing-boynton-beach-fl': { city: 'Boynton Beach' },
  '/auto-detailing-deerfield-beach-fl': { city: 'Deerfield Beach' },
  '/auto-detailing-boca-point-fl': { city: 'Boca Point' },
  '/auto-detailing-boca-raton-fl': { city: 'Boca Raton' },
  '/auto-detailing-pompano-beach-fl': { city: 'Pompano Beach' },
  '/auto-detailing-west-boca-fl': { city: 'West Boca' },
  '/auto-detailing-coconut-creek-fl': { city: 'Coconut Creek' },
  '/auto-detailing-coral-springs-fl': { city: 'Coral Springs' },
  '/auto-detailing-mission-bay-fl': { city: 'Mission Bay' },
  '/auto-detailing-lighthouse-point-fl': { city: 'Lighthouse Point' },
};

/* Build the final routeConfig map: { path: { title, description, keywords, canonical, schema[] } } */

const routeConfig = {};

/* Home - already has schemas injected separately; provide fallback canonical */
routeConfig['/'] = {
  title: 'Car Detailing, Ceramic Coating & PPF in Boca Raton, FL',
  description:
    "Premium ceramic coating, paint protection film, detailing, window tint, and car wrapping in Boca Raton. Get a free quote today.",
  keywords:
    'car detailing South Florida, ceramic coating Boca Raton, paint protection film Boca Raton, PPF South Florida, window tint Boca Raton, ceramic tint Florida, mobile car detailing Boca Raton, Kovalent ceramic coating, STEK PPF, best car detailer South Florida, luxury car detailing Palm Beach County, exotic car detailing Broward County, auto detailing Fort Lauderdale, ceramic coating Delray Beach, PPF Fort Lauderdale, window tinting Boynton Beach, mobile detailing Parkland, ceramic coating West Palm Beach, car wrapping Florida, yacht detailing Florida, fleet detailing South Florida',
  canonical: '/',
  schema: [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: SITE_URL,
      name: 'SSF Detail',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: [
        'Car Detailing',
        'Ceramic Coating',
        'Paint Protection Film',
        'Window Tinting',
        'Car Wrapping',
        'Marine Detailing',
        'Plane Detailing',
        'Corporate Fleet',
        'Reviews',
        'Shop',
        'Blog',
        'Contact',
      ],
      url: [
        `${SITE_URL}/car-detailing`,
        `${SITE_URL}/ceramic-coating`,
        `${SITE_URL}/paint-protection-film`,
        `${SITE_URL}/window-tint`,
        `${SITE_URL}/car-wrapping`,
        `${SITE_URL}/marine-detailing`,
        `${SITE_URL}/plane-detailing`,
        `${SITE_URL}/corporate-fleet`,
        `${SITE_URL}/reviews`,
        `${SITE_URL}/shop`,
        `${SITE_URL}/blogs`,
        `${SITE_URL}/contact-us`,
      ],
    },
  ],
};

/* Service pages */
Object.entries(SERVICE_PAGES).forEach(([path, cfg]) => {
  const schemas = [
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: cfg.schemaName || cfg.title, path },
    ]),
  ];
  if (cfg.schemaName) {
    schemas.push(
      serviceSchema({
        name: cfg.schemaName,
        description: cfg.schemaDesc,
        serviceType: cfg.schemaType,
        path,
        priceRange: cfg.priceRange,
        offers: cfg.offers,
      })
    );
  }
  routeConfig[path] = {
    title: cfg.title,
    description: cfg.description,
    keywords: cfg.keywords,
    canonical: path,
    schema: schemas,
  };
});

routeConfig['/window-tint'].ogImage = `${SITE_URL}/window-tint/porsche-1500.webp`;
routeConfig['/window-tint'].schema.find((item) => item['@type'] === 'Service').image = `${SITE_URL}/window-tint/porsche-1500.webp`;

/* Location pages */
Object.entries(LOCATION_PAGES).forEach(([path, { city }]) => {
  routeConfig[path] = {
    title: `Auto Detailing in ${city}, FL | Ceramic Coating, PPF & Tint | SSF Detail`,
    description: `Mobile and shop-based auto detailing, Kovalent ceramic coating, STEK paint protection film, and window tinting serving ${city}, FL. 8+ years of experience, 150+ 5-star Google reviews, and a 12-month warranty on every installation.`,
    keywords: `auto detailing ${city} FL, car detailing ${city}, ceramic coating ${city}, PPF ${city}, window tint ${city}, mobile detailing ${city}, luxury car detailing ${city}, paint protection film ${city}, car detailer near me ${city}, best detailer ${city} Florida`,
    canonical: path,
    schema: [
      breadcrumb([
        { name: 'Home', path: '/' },
        { name: `Auto Detailing in ${city}, FL`, path },
      ]),
      locationSchema({ city, path }),
    ],
  };
});

/* Postcard campaign — public video-first landing page with standard site chrome. */
routeConfig['/postcard'] = {
  title: 'Welcome to SSF Detail | South Florida Ceramic Coating & PPF',
  description: "Welcome to SSF Detail, South Florida's premier ceramic coating and PPF studio. Watch our welcome video, compare coating packages, and request a free quote.",
  canonical: '/postcard',
  ogImage: `${SITE_URL}/postcard/welcome-poster.webp`,
  schema: [
    breadcrumb([{ name: 'Home', path: '/' }, { name: 'Welcome to SSF Detail', path: '/postcard' }]),
    {
      '@context': 'https://schema.org', '@type': 'VideoObject',
      name: 'Welcome to SSF Detail!',
      description: "A welcome from SSF Detail — South Florida's Premier Ceramic Coating and PPF Studio.",
      thumbnailUrl: `${SITE_URL}/postcard/welcome-poster.webp`,
      contentUrl: `${SITE_URL}/postcard/welcome.mp4`,
      uploadDate: '2026-09-24T00:00:00Z',
      duration: 'PT1M',
      publisher: { '@type': 'Organization', name: 'SSF Detail', url: SITE_URL },
    },
  ],
};

/* Contact */
routeConfig['/postcard-terms'] = {
  title: 'Postcard Offer Terms & Conditions | SSF Detail',
  description: 'Terms and conditions for the SSFDetail buy one, get one ceramic coating postcard offer. Both vehicles must be completed by October 30, 2026.',
  canonical: '/postcard-terms',
  schema: [breadcrumb([{ name: 'Home', path: '/' }, { name: 'Postcard Offer', path: '/postcard' }, { name: 'Offer Terms & Conditions', path: '/postcard-terms' }])],
};

/* Contact */
routeConfig['/contact-us'] = {
  title: 'Contact SSF Detail | Free Quote for Car Detailing in Boca Raton, FL',
  description:
    'Get a free quote from SSF Detail in Boca Raton. Request car detailing, ceramic coating, PPF, window tint, marine, and plane detailing. Call 561-489-7527 or submit our online form.',
  keywords:
    'contact SSF Detail, car detailing quote Boca Raton, free detailing quote Florida, car wash appointment Boca Raton',
  canonical: '/contact-us',
  schema: [
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact-us' },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact SSF Detail',
      url: `${SITE_URL}/contact-us`,
      mainEntity: PROVIDER,
    },
  ],
};
routeConfig['/contact'] = routeConfig['/contact-us'];

/* About Us — hidden from main nav but reachable via Reviews CTA. */
routeConfig['/about-us'] = {
  title: 'About SSF Detail | Luxury Car Detailing & Ceramic Coating Boca Raton',
  description:
    "Meet SSF Detail — South Florida's premier luxury auto detailing studio in Boca Raton, FL. Owned by Steven Shames, we deliver expert ceramic coating, PPF, window tint and premium detailing. 1,350+ vehicles serviced. 150+ five-star reviews.",
  keywords:
    'about SSF Detail, Steven Shames, luxury car detailing Boca Raton, ceramic coating South Florida, PPF Boca Raton, best detailing shop Palm Beach County, boca raton ceramic coating specialist',
  canonical: '/about-us',
  schema: [
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'About Us', path: '/about-us' },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About SSF Detail',
      url: `${SITE_URL}/about-us`,
      mainEntity: {
        ...PROVIDER,
        founder: {
          '@type': 'Person',
          name: 'Steven Shames',
          jobTitle: 'CEO & Owner',
          worksFor: { '@type': 'LocalBusiness', name: 'SSF Detail' },
        },
      },
    },
  ],
};

/* Executive */
routeConfig['/executive'] = {
  title: 'Executive Member Services | Discreet Luxury Detailing | SSF Detail',
  description:
    'Executive concierge car detailing services in Boca Raton for clients requiring discretion, NDAs, after-hours availability, and verified detailers for exotic and luxury vehicles.',
  keywords:
    'executive car detailing Boca Raton, concierge detailing South Florida, NDA detailing services, private car detailing Florida',
  canonical: '/executive',
};

/* Shop */
routeConfig['/shop'] = {
  title: 'Shop SSF Detail | Premium Car Care Products | Boca Raton, FL',
  description:
    'Shop premium car care products from SSF Detail including ceramic coatings, maintenance kits, and detailing accessories used by our Boca Raton professionals.',
  keywords:
    'SSF Detail shop, car care products Florida, ceramic coating maintenance kit, detailing supplies Boca Raton',
  canonical: '/shop',
};

/* Blogs */
routeConfig['/blogs'] = {
  title: 'Car Care Blog | Detailing, Ceramic Coating & PPF Tips | SSF Detail',
  description:
    'Expert articles on car detailing, ceramic coating, paint protection film, and window tinting from the SSF Detail team in Boca Raton, FL.',
  keywords:
    'car detailing blog, ceramic coating guide, PPF vs ceramic coating, window tint guide, car care tips Florida',
  canonical: '/blogs',
};

/* Reviews — dedicated page with individual Review schema for rich star snippets */
routeConfig['/reviews'] = {
  title: 'SSF Detail Reviews | 150+ 5-Star Verified Google Reviews | Boca Raton, FL',
  description:
    "Read verified 5-star reviews from South Florida's most demanding vehicle owners. See why SSF Detail is Boca Raton's top-rated ceramic coating, PPF, window tint, and luxury car detailing studio — 150+ Google reviews, 1,350+ vehicles serviced.",
  keywords:
    'SSF Detail reviews, ceramic coating reviews Boca Raton, PPF reviews South Florida, car detailing reviews Palm Beach County, best detailer reviews Florida, verified Google reviews SSF',
  canonical: '/reviews',
  schema: [
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Reviews', path: '/reviews' },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/reviews#page`,
      name: 'SSF Detail — Customer Reviews',
      url: `${SITE_URL}/reviews`,
      description:
        '150+ verified 5-star Google reviews for SSF Detail — luxury car detailing, ceramic coating, PPF, and window tint in Boca Raton, FL.',
      about: { '@id': 'https://ssfdetail.com/#business' },
      mainEntity: {
        '@type': 'LocalBusiness',
        '@id': 'https://ssfdetail.com/#business',
        name: 'SSF Detail',
        aggregateRating: RATING,
        review: reviewsData.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.author },
          datePublished: r.datePublished,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
          reviewBody: r.body,
          itemReviewed: {
            '@type': 'LocalBusiness',
            name: 'SSF Detail',
          },
        })),
      },
    },
  ],
};

/* Privacy / Terms */
routeConfig['/privacy-policy'] = {
  title: 'Privacy Policy | SSF Detail',
  description: 'Privacy policy for SSF Detail, Boca Raton, FL.',
  canonical: '/privacy-policy',
};
routeConfig['/terms-and-conditions'] = {
  title: 'Terms & Conditions | SSF Detail',
  description: 'Terms and conditions for SSF Detail services in Boca Raton, FL.',
  canonical: '/terms-and-conditions',
};

/* Blog posts — Article / BlogPosting schema for rich search results */
const BLOG_PAGES = {
  '/blog/services-boca-raton': {
    headline: '#1 Ceramic Coating, Car Detailing & PPF In Boca Raton, FL',
    description: 'Discover the top-rated ceramic coating, car detailing, and paint protection film services in Boca Raton, FL from SSF Detail.',
    keywords: 'ceramic coating Boca Raton, car detailing Boca Raton, PPF Boca Raton, best detailer Boca Raton',
    datePublished: '2026-01-03',
    dateModified: '2026-01-15',
  },
  '/blog/faqs': {
    headline: 'Car Detailing, Ceramic Coating & PPF FAQs',
    description: 'Answers to the most common questions about ceramic coating, paint protection film, window tinting, and car detailing from SSF Detail in Boca Raton, FL.',
    keywords: 'car detailing FAQ, ceramic coating FAQ, PPF FAQ, window tint FAQ Boca Raton',
    datePublished: '2026-01-03',
    dateModified: '2026-01-15',
  },
  '/blog/ceramic-coating-boca-raton': {
    headline: '#1 Ceramic Coating In Boca Raton, Florida',
    description: 'Learn why SSF Detail is the #1 choice for ceramic coating in Boca Raton, FL — Kovalent coatings, professional installation, and a 12-month warranty.',
    keywords: 'ceramic coating Boca Raton, Kovalent ceramic coating, best ceramic coating Florida, nano ceramic Boca Raton',
    datePublished: '2026-01-03',
    dateModified: '2026-01-15',
  },
  '/blog/car-detailing-boca-raton': {
    headline: 'Top-Rated Car Detailing in Boca Raton, Florida',
    description: 'Why SSF Detail is the top-rated car detailing company in Boca Raton, FL — interior, exterior, paint correction, and concierge mobile service.',
    keywords: 'car detailing Boca Raton, top rated detailer Florida, mobile detailing Boca Raton, paint correction Boca Raton',
    datePublished: '2026-01-09',
    dateModified: '2026-01-15',
  },
  '/blog/ppf-boca-raton': {
    headline: 'Top-Rated PPF in Boca Raton, Florida',
    description: 'Top-rated STEK paint protection film installation in Boca Raton, FL. Self-healing, hydrophobic, impact-resistant PPF for luxury and exotic vehicles.',
    keywords: 'PPF Boca Raton, STEK paint protection film, clear bra Boca Raton, paint protection Florida',
    datePublished: '2026-01-09',
    dateModified: '2026-01-15',
  },
  '/blog/window-tinting': {
    headline: 'Premium Window Tinting with Autobahn Films',
    description: 'Premium automotive window tinting with Autobahn ceramic films in Boca Raton, FL. Heat rejection, UV protection, and crystal-clear visibility.',
    keywords: 'window tint Boca Raton, Autobahn window tint, ceramic window tint, automotive tinting Florida',
    datePublished: '2026-01-09',
    dateModified: '2026-01-15',
  },
  '/blog/window-tint-brands': {
    headline: 'Choosing the Right Brand of Window Tint in Boca Raton',
    description: 'A guide to choosing the right window tint brand in Boca Raton, FL — comparing Autobahn, 3M, SunTek, XPEL, and Llumar for performance and warranty.',
    keywords: 'window tint brands, best window tint Florida, Autobahn vs 3M tint, ceramic tint comparison',
    datePublished: '2025-09-11',
    dateModified: '2025-12-01',
  },
  '/blog/ceramic-coating-why': {
    headline: 'Why Boca Raton Vehicle Owners Choose Ceramic Coating',
    description: 'Discover why luxury and exotic vehicle owners in Boca Raton, FL choose ceramic coating to protect their investment and maintain showroom-quality paint.',
    keywords: 'why ceramic coating, ceramic coating benefits, ceramic coating Boca Raton, luxury car protection',
    datePublished: '2025-08-26',
    dateModified: '2025-12-01',
  },
  '/blog/ceramic-coating-team': {
    headline: "Meet the Ceramic Coating Team Protecting Your Vehicle's Paint Like New",
    description: 'Meet the SSF Detail ceramic coating team in Boca Raton, FL — certified installers protecting luxury and exotic vehicles with Kovalent coatings.',
    keywords: 'ceramic coating team, certified ceramic installer, SSF Detail team, professional ceramic coating',
    datePublished: '2025-08-26',
    dateModified: '2025-12-01',
  },
  '/blog/ppf-vs-ceramic': {
    headline: 'Is Paint Protection Film or Ceramic Coating Better for Your Vehicle?',
    description: 'PPF vs ceramic coating — a complete comparison of paint protection film and ceramic coating to help you choose the right protection for your vehicle.',
    keywords: 'PPF vs ceramic coating, paint protection film comparison, which is better PPF or ceramic, car paint protection',
    datePublished: '2025-07-24',
    dateModified: '2025-12-01',
  },
  '/blog/top-5-reasons': {
    headline: "Top 5 Reasons Florida's Residents Choose Our Car Detailing Services",
    description: 'Top 5 reasons Florida residents choose SSF Detail for car detailing — premium products, certified team, mobile service, 12-month warranty, and 150+ 5-star reviews.',
    keywords: 'best car detailing Florida, why choose SSF Detail, top detailing service Boca Raton, Florida car care',
    datePublished: '2025-04-30',
    dateModified: '2025-12-01',
  },
  '/blog/ceramic-coating-cost': {
    headline: 'How Much is Ceramic Coating in Florida? A Cost Overview for Vehicles',
    description: 'How much does ceramic coating cost in Florida? A complete cost breakdown for sedans, SUVs, trucks, and exotic vehicles in Boca Raton and South Florida.',
    keywords: 'ceramic coating cost Florida, how much is ceramic coating, ceramic coating price Boca Raton, car coating cost',
    datePublished: '2025-03-21',
    dateModified: '2025-12-01',
  },
  '/blog/protect-car-florida': {
    headline: "How to Protect Your Car from Florida's Intense Sunlight and Salty Air",
    description: "How to protect your car from Florida's UV rays, humidity, and salty air — ceramic coating, PPF, window tint, and detailing tips from SSF Detail in Boca Raton.",
    keywords: 'protect car Florida, UV protection car, salt air car damage, Florida car care, sun protection vehicle',
    datePublished: '2025-01-27',
    dateModified: '2025-12-01',
  },
  '/blog/detailing-cost-florida': {
    headline: 'How Much Does Car Detailing Cost in Florida?',
    description: 'How much does car detailing cost in Florida? A breakdown of detailing pricing in Boca Raton and South Florida for cars, SUVs, and trucks.',
    keywords: 'car detailing cost Florida, how much is car detailing, detailing prices Boca Raton, auto detail cost',
    datePublished: '2024-12-24',
    dateModified: '2025-12-01',
  },
  '/blog/paint-protection-film-complete-guide': {
    headline: 'STEK Paint Protection Film Complete Guide: Coverage, Warranty & Installation in Boca Raton, FL (2026)',
    description: 'Complete STEK paint protection film (PPF) guide for Boca Raton, FL — all coverage tiers (Starter Kit, Full Front End, Track Package, Full Body), 10-year warranty, self-healing technology, install times, and FAQs from SSF Detail. Request a personalized quote.',
    keywords: 'paint protection film Boca Raton, STEK PPF, clear bra Florida, PPF vs ceramic coating, self-healing PPF, PPF installer Boca Raton, best PPF Florida, full body PPF, track package PPF',
    datePublished: '2026-08-17',
    dateModified: '2026-08-17',
  },
  '/blog/kovalent-ceramic-coating-complete-guide': {
    headline: 'Kovalent Ceramic Coating Complete Guide: Prolong, Graphene & Borophene Tiers Explained (Boca Raton, FL 2026)',
    description: 'Complete Kovalent ceramic coating guide for Boca Raton, FL — all 5 tiers explained (Prolong Light 3-year, Prolong 5-year, Graphene 7-year, Matrix 9-year, Borophene 9-year), application process, maintenance, warranty, and FAQs from SSF Detail. Request a personalized quote.',
    keywords: 'Kovalent ceramic coating, ceramic coating Boca Raton, graphene ceramic coating, borophene ceramic, 9 year ceramic coating, ceramic coating tiers, best ceramic coating Florida, nano ceramic coating, professional ceramic installer',
    datePublished: '2026-08-17',
    dateModified: '2026-08-17',
  },
  '/blog/mobile-detailing-membership-packages': {
    headline: 'SSF Detail Membership & Mobile Detailing Packages in South Florida (2026 Benefits Guide)',
    description: 'Complete guide to SSF Detail membership tiers (Essential, Signature, Executive) and mobile detailing packages (Ceramic-Safe Wash, Maintenance Detail, Full Detail, Full Detail PLUS) across Boca Raton, Palm Beach, Broward, and Tampa Bay. Request a personalized quote.',
    keywords: 'mobile detailing Boca Raton, car detailing membership Florida, mobile car wash South Florida, executive detailing membership, corporate fleet detailing, mobile ceramic coating maintenance, SSF Detail membership, luxury car maintenance plan',
    datePublished: '2026-08-17',
    dateModified: '2026-08-17',
  },
};

const BLOG_DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_carcare-elite/artifacts/vb3klkvj_Award%20Winning%20PPF%20And%20Ceramic%20Coating%20In%20Boca%20Raton%2C%20FL%20%20SSF%20Detail.png';

const blogPostingSchema = ({ headline, description, path, datePublished, dateModified }) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline,
  description,
  image: BLOG_DEFAULT_IMAGE,
  author: {
    '@type': 'Organization',
    name: 'SSF Detail',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'SSF Detail',
    logo: {
      '@type': 'ImageObject',
      url: 'https://customer-assets.emergentagent.com/job_3e5f5ea8-250b-4689-9d84-3ea209468493/artifacts/zno00h4h_ssf%20detail%20red%20logo-Picsart-BackgroundRemover.jfif',
    },
  },
  datePublished: datePublished || '2025-01-15',
  dateModified: dateModified || '2025-06-01',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}${path}`,
  },
  url: `${SITE_URL}${path}`,
  articleSection: 'Car Care',
  inLanguage: 'en-US',
});

Object.entries(BLOG_PAGES).forEach(([path, cfg]) => {
  routeConfig[path] = {
    title: `${cfg.headline} | SSF Detail Blog`,
    description: cfg.description,
    keywords: cfg.keywords,
    canonical: path,
    schema: [
      breadcrumb([
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blogs' },
        { name: cfg.headline, path },
      ]),
      blogPostingSchema({
        headline: cfg.headline,
        description: cfg.description,
        path,
        datePublished: cfg.datePublished,
        dateModified: cfg.dateModified,
      }),
    ],
  };
});

/* Per-service-page FAQ content (mirrors visible FAQ on the page).
 * Plain-text answers are required by Google for FAQPage schema. */
const SERVICE_FAQ_TEXTS = {
  '/ceramic-coating': [
    { q: 'How long does ceramic coating last?', a: "Professional ceramic coating typically lasts 2 to 9 years depending on the formulation. At SSF Detail we install Kovalent Ceramic Coatings with tiered durability: Prolong Light (3 years), Prolong (5 years), Graphene (7 years, 2-layer), and Matrix or Borophene (9 years). Lifespan depends on the product, environmental exposure, and proper maintenance." },
    { q: 'How much does ceramic coating cost in Florida?', a: 'Professional ceramic coating in Boca Raton typically ranges from $399 for an entry-level 3-year coating to $2,999+ for a premium 9-year multi-layer coating with paint correction included. Pricing varies by vehicle size (sedan/SUV/truck), the coating tier you select, and whether paint correction is required to remove existing swirl marks before application.' },
    { q: 'Does ceramic coating prevent scratches?', a: 'Ceramic coating significantly reduces minor scratches and swirl marks from improper washing, but it is not bulletproof. It will not stop rock chips or deep abrasions. For physical impact protection, paint protection film (PPF) is the right product. Many of our clients pair STEK PPF on high-impact areas with a Kovalent ceramic coating layered on top for the best of both worlds.' },
    { q: 'How do I maintain a ceramic coated car?', a: 'Ceramic coating maintenance is minimal but important. Wash every 2 weeks with a pH-neutral shampoo using the two-bucket method, dry with a clean microfiber towel, and avoid automatic brush car washes. Apply a ceramic-safe spray detailer monthly to refresh hydrophobic properties. Avoid waxing — it can degrade the coating. Annual inspections at SSF Detail keep your warranty valid.' },
    { q: "What's the difference between Kovalent Prolong, Graphene, and Borophene?", a: 'Kovalent Prolong is a traditional silica-based ceramic coating offered in 3 and 5 year durability. Kovalent Graphene incorporates graphene oxide for a stronger, more hydrophobic, scratch-resistant 7-year coating. Kovalent Matrix and Borophene are our top tiers using advanced graphene and boron nitride chemistry for 9 years of protection with self-healing and superior scratch resistance.' },
  ],
  '/paint-protection-film': [
    { q: 'How long does paint protection film (PPF) last?', a: 'STEK paint protection film typically lasts 7 to 10 years with proper care. STEK PPF is engineered for harsh climates like Florida — UV-stable, hydrophobic, and self-healing. Our installations carry a 10-year manufacturer warranty against yellowing, cracking, and delamination, plus our SSF Detail 12-month installation warranty.' },
    { q: 'How much does PPF cost in Boca Raton?', a: 'PPF pricing depends on coverage area and vehicle. Starter Kits (front bumper + mirrors) start in the lower price range, Full Front End Kits add hood, fenders, and headlights, Track Packages add A-pillars and rear bumper, and Full Body Kits cover the entire vehicle. Get a free custom quote based on your vehicle and desired coverage.' },
    { q: 'Does PPF self-heal?', a: 'Yes — STEK paint protection film features self-healing properties. Light scratches and swirl marks disappear with heat exposure (sunlight or warm water). The film also resists rock chips, road debris, bug etching, water spots, and UV damage. The clear, high-gloss finish maintains the appearance of your factory paint.' },
    { q: 'PPF vs ceramic coating — which is better?', a: 'They protect against different things. PPF is a physical urethane film that absorbs rock chip impacts and prevents scratches. Ceramic coating is a liquid nano-layer that adds gloss, hydrophobic water beading, and UV protection but offers minimal impact defense. The best protection combines both: STEK PPF on impact zones (front end, rockers) plus a Kovalent ceramic coating layered on top for full-vehicle hydrophobic gloss.' },
    { q: 'Can PPF be removed?', a: 'Yes — paint protection film is fully removable without damaging your factory paint, when removed by a professional. STEK PPF uses a removable adhesive specifically designed to lift cleanly even after years of installation. Avoid removing PPF yourself — improper removal can pull factory paint, especially around edges. SSF Detail performs professional PPF removal as a service.' },
  ],
  '/car-detailing': [
    { q: 'How often should I detail my car?', a: 'For most vehicles in South Florida we recommend a maintenance detail every 4 to 6 weeks and a full detail every 6 months. Florida sun, humidity, salt, and pollen accelerate paint contamination, interior wear, and odor buildup. Daily drivers benefit from monthly maintenance; garage-kept exotics can stretch to quarterly.' },
    { q: "What's included in a full car detail at SSF Detail?", a: 'A full detail includes a thorough exterior wash with the two-bucket method, decontamination (iron remover and clay bar), wheel and tire detailing, full interior vacuum, steam cleaning of carpets and upholstery, leather conditioning, dash and trim treatment, glass cleaning inside and out, and a hand-applied paint sealant. Add-ons include paint correction and ceramic coating.' },
    { q: 'How long does a full detail take?', a: 'A maintenance detail typically takes 2 to 3 hours. A full interior + exterior detail takes 4 to 6 hours. Detail with paint correction takes 1 to 2 days. Detail with paint correction + ceramic coating takes 2 to 4 days. We provide Mercedes AMG loaners for projects exceeding 2 days at our shop.' },
    { q: "What's the difference between a car wash and a full detail?", a: 'A car wash is a surface cleaning of the exterior — usually under 30 minutes. A full detail is a multi-hour deep clean that addresses contamination embedded in the paint, restores interior surfaces, removes odors, and includes protective products like sealants or coatings. A wash maintains; a detail restores.' },
    { q: 'Do you come to my home or do I need to bring the car to your shop?', a: 'Both options are available. SSF Detail operates a full climate-controlled shop at 850 NW 1st Ave in Boca Raton, FL, plus fully mobile detailing units that travel to your home, office, dealership, gym, yacht club, or private garage across South Florida and Tampa Bay. Same quality, same products, same 12-month warranty either way.' },
  ],
  '/window-tint': TINT_FAQS,
  /* The 4 newest service pages reuse the FAQ data directly (aText -> a). */
  '/marine-detailing': marineDetailingFAQ.map((it) => ({ q: it.q, a: it.aText })),
  '/plane-detailing': planeDetailingFAQ.map((it) => ({ q: it.q, a: it.aText })),
  '/car-wrapping': carWrappingFAQ.map((it) => ({ q: it.q, a: it.aText })),
  '/corporate-fleet': corporateFleetFAQ.map((it) => ({ q: it.q, a: it.aText })),
};

/* Sales / pre-sell page reuses the ceramic FAQ — keep the schemas in sync. */
SERVICE_FAQ_TEXTS['/about-ceramic-coating'] = SERVICE_FAQ_TEXTS['/ceramic-coating'];

/* FAQPage schema for the 3 deep-dive / AI-first blog posts.
   Answers mirror the visible H3 questions on each post so LLMs and
   search engines can extract the same content that a human sees. */
SERVICE_FAQ_TEXTS['/blog/paint-protection-film-complete-guide'] = [
  { q: 'What is Paint Protection Film (PPF)?', a: "Paint Protection Film is a clear, elastomeric thermoplastic polyurethane (TPU) film installed over a vehicle's factory paint. Modern PPF like STEK Dynoshield is roughly 8 mils (200 microns) thick, self-healing when exposed to heat, UV-stable, and hydrophobic. It absorbs impacts from rock chips and road debris that would otherwise damage factory paint." },
  { q: 'How much does PPF cost in Boca Raton?', a: 'SSF Detail STEK PPF pricing is quoted per vehicle after a walk-around inspection. Four coverage tiers are available: Starter Kit (front bumper, mirrors, door edges), Full Front End (bumper, hood, fenders, headlights, mirrors), Track Package (adds A-pillars, rockers, rear bumper), and Full Body PPF. Final quote depends on vehicle size and film choice. Request a free personalized quote at ssfdetail.com/contact-us.' },
  { q: 'How long does PPF last and what is the warranty?', a: 'STEK PPF carries a 10-year manufacturer warranty against yellowing, cracking, bubbling, and delamination. SSF Detail adds a 12-month installation warranty. With proper maintenance, PPF commonly performs at spec for 8-10 years in Florida conditions.' },
  { q: 'How does self-healing PPF work?', a: 'Modern PPF uses an elastomeric TPU top-coat with shape-memory properties. When scratched, ambient heat (sun, warm water, heat gun) causes the polymer chains to relax back to their original position, erasing light surface marring within minutes.' },
  { q: 'Is PPF worth it in Florida?', a: "Yes. Florida's combination of highway rock chips, intense UV, salt air, love bugs, and afternoon rains is one of the harshest environments for factory paint. PPF is the only product that physically prevents rock chips and preserves resale value on luxury vehicles." },
  { q: 'Can PPF be removed?', a: 'Yes — STEK PPF uses a removable adhesive designed to release cleanly from factory paint. Removal must be performed by a professional to avoid pulling paint at edges. SSF Detail performs PPF removal as a service.' },
];

SERVICE_FAQ_TEXTS['/blog/kovalent-ceramic-coating-complete-guide'] = [
  { q: 'What is Kovalent ceramic coating?', a: "Kovalent is a professional-grade nano-ceramic coating system using SiO2 (silica dioxide) chemistry, plus graphene oxide and boron nitride reinforcement in higher tiers. It forms a permanent covalent bond with a vehicle's clear coat, creating a hydrophobic, UV-stable, chemical-resistant sacrificial layer that lasts 3-9 years." },
  { q: 'What are the Kovalent ceramic coating tiers?', a: 'SSF Detail offers five Kovalent tiers: Prolong Light (3-year), Prolong (5-year), Graphene (7-year, best seller with paint correction included), Matrix (9-year with multi-stage correction), and Borophene (9-year top-tier with boron-nitride reinforcement). Every install includes a personalized quote based on your vehicle, tier, and paint-correction scope.' },
  { q: 'How is graphene ceramic different from silica ceramic?', a: 'Standard silica ceramics protect against UV and contamination but are thermally inert. Graphene-infused ceramics conduct heat away from the surface (reducing water spotting on hot Florida paint), have higher hardness, and repel water more aggressively. Borophene extends that with an even harder finish and self-cleaning behavior.' },
  { q: 'Does ceramic coating prevent rock chips?', a: 'No. Ceramic coating is a chemical layer that protects against contamination and UV, not physical impact. For rock-chip protection, install STEK Paint Protection Film on the front end and layer the ceramic coating over it.' },
  { q: 'How do I maintain a Kovalent ceramic coating?', a: 'Hand wash every 2 weeks with pH-neutral shampoo (two-bucket method), dry with plush microfiber, avoid brush car washes, use a ceramic-safe spray detailer monthly, never wax, and bring the vehicle back once per year for the SSF Detail maintenance visit that keeps the warranty active.' },
  { q: 'How long after ceramic coating install can I wash the car?', a: 'Wait 7 days before washing to let the coating fully cure. Avoid heavy rain the day of install; light rain is fine after 24 hours.' },
  { q: 'Does ceramic coating work on matte paint?', a: 'Yes. Kovalent offers matte-compatible formulations that preserve the matte finish while adding hydrophobic and chemical-resistance benefits. Never apply a gloss-enhancing ceramic on matte paint.' },
];

SERVICE_FAQ_TEXTS['/blog/mobile-detailing-membership-packages'] = [
  { q: 'What are the SSF Detail membership tiers?', a: 'SSF Detail offers three membership tiers: Essential Member (one Ceramic-Safe Wash monthly, member discounts on upgrades), Signature Member (two Maintenance Details monthly, quarterly decontamination, enhanced member discounts, annual ceramic warranty inspection), and Executive Member (unlimited on-demand mobile detailing, dedicated detailer, executive-tier member discounts on ceramic and PPF, complimentary marine/aircraft quotes). Contact the concierge at 561-489-7527 for a personalized quote.' },
  { q: 'What mobile detailing packages does SSF Detail offer?', a: 'Four à la carte packages: Ceramic-Safe Car Wash (60-90 min), Maintenance Detail (2-3 hours, interior + exterior), Full Detail (4-6 hours with iron remover, clay bar, leather conditioning, paint sealant), and Full Detail PLUS (6-8 hours with steam cleaning, deep odor treatment, graphene paint sealant). Every service begins with a personalized quote.' },
  { q: 'Where does SSF Detail provide mobile detailing?', a: 'SSF Detail operates a fully equipped mobile fleet across Boca Raton, Boca Point, West Boca, Mission Bay, Boynton Beach, Deerfield Beach, Pompano Beach, Coconut Creek, Coral Springs, Lighthouse Point, Delray Beach, Parkland, Wellington, Jupiter, Hillsboro Beach, Aventura, Fort Lauderdale, West Palm Beach, Miami, and the Greater Tampa Bay area.' },
  { q: 'Is mobile detailing the same quality as in-shop detailing?', a: 'Yes. SSF Detail mobile units carry commercial-grade water tanks, power, foam cannons, steam machines, and the same Koch-Chemie, Kovalent, and STEK products used in the Boca Raton shop. Ceramic coating installs and paint correction requiring controlled-environment cure are performed in-shop.' },
  { q: 'Can I cancel or pause my membership?', a: 'Yes — memberships are month-to-month with no long-term contract. Pause during travel or seasonal absence and resume when you return. Contact the concierge at 561-489-7527.' },
  { q: 'Do memberships include ceramic coating or PPF?', a: 'Memberships include maintenance — the ceramic coating or PPF installation is a separate service. However, all member tiers receive a member discount on Kovalent ceramic coating and STEK PPF installations, and Executive Members receive complimentary quotes for marine and aircraft services.' },
  { q: 'Do you offer corporate or dealership memberships?', a: 'Yes. SSF Detail offers a Corporate & Fleet program for dealership PDI programs, corporate car fleets, and multi-vehicle discounts. Dedicated account manager included.' },
];

const buildFAQPageSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
});

/* Append FAQPage schema to service pages that have a visible FAQ section */
Object.entries(SERVICE_FAQ_TEXTS).forEach(([path, items]) => {
  if (routeConfig[path]) {
    routeConfig[path].schema = [
      ...(routeConfig[path].schema || []),
      buildFAQPageSchema(items),
    ];
  }
});

export default routeConfig;
